// src/services/geminiService.js
import Vue from 'vue';

// Configuration
// const API_URL = process.env.VUE_APP_GEMINI_API_URL;
const API_URL = process.env.VUE_APP_GEMINI_API_URL;
const TYPE = process.env.VUE_APP_GEMINI_TYPE;
const DEFAULT_MODEL = 'gemini-2.5-flash';

// Common prompt templates
const ARTICLE_STYLE_INSTRUCTION = `
Use an informative, light and narrative style as commonly found in popular media articles 
such as Mojok, Vice Indonesia, or Kumparan. 
Use a storytelling style as if written by a human — don't be too stiff or formal. 
Make sentences of varying lengths, use idioms or a style of speech typical of young people. 
Avoid overly repetitive sentence structures and overly neat formal language. 
Insert light opinions or general observations to make it feel more alive. 
Make sure the result passes the plagiarism check and AI generated article check. and using Indonesian Language
`;

const OUTPUT_FORMAT_INSTRUCTION = `
Output Format Requirements:
- Do not include Markdown or triple backticks or any other special characters.
- Use appropriate HTML tags such as <p>, <h2>, <ul>, <li>, etc.
`;

const OUTPUT_FORMAT_INSTRUCTION_FOR_YOUTUBE_LINK = `
Output Format Requirements:
- Do not include Markdown or triple backticks or any other special characters.
`;

/**
 * Makes API request to Gemini service
 * @param {Object} requestData - Request payload
 * @returns {Promise} API response
 */
async function makeGeminiRequest(requestData, maxRetries = 1) {
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await fetch(`${API_URL}/api/gemini/stream`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: DEFAULT_MODEL,
          type: TYPE,
          ...requestData,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get a reader for the stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);

          fullText += chunk;
        }
      } finally {
        // Always release the reader
        reader.releaseLock();
      }

      // Return the accumulated text or fallback message
      return fullText;
    } catch (error) {
      console.error(`Attempt ${attempt}/${maxRetries} failed:`, error);

      // Handle different types of errors
      let errorMessage = 'Unknown error occurred';
      let errorCode = null;
      let statusCode = null;
      let shouldRetry = false;

      // Network/Fetch errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage =
          'Network connection failed. Please check your internet connection.';
        errorCode = 'NETWORK_ERROR';
        shouldRetry = true;
      }
      // AbortError (request timeout/cancelled)
      else if (error.name === 'AbortError') {
        errorMessage = 'Request was cancelled or timed out.';
        errorCode = 'REQUEST_CANCELLED';
        shouldRetry = true;
      }
      // HTTP Response errors
      else if (error.message?.includes('HTTP error!')) {
        const statusMatch = error.message.match(/status: (\d+)/);
        statusCode = statusMatch ? parseInt(statusMatch[1]) : null;

        switch (statusCode) {
          case 400:
            errorMessage = 'Bad request. Please check your input parameters.';
            errorCode = 'BAD_REQUEST';
            shouldRetry = false;
            break;
          case 401:
            errorMessage = 'Unauthorized. Please check your API credentials.';
            errorCode = 'UNAUTHORIZED';
            shouldRetry = false;
            break;
          case 403:
            errorMessage =
              "Forbidden. You don't have permission to access this resource.";
            errorCode = 'FORBIDDEN';
            shouldRetry = false;
            break;
          case 404:
            errorMessage = 'API endpoint not found.';
            errorCode = 'NOT_FOUND';
            shouldRetry = false;
            break;
          case 429:
            errorMessage = 'Too many requests. Please try again later.';
            errorCode = 'RATE_LIMITED';
            shouldRetry = true;
            break;
          case 500:
            errorMessage = 'Internal server error. Please try again later.';
            errorCode = 'SERVER_ERROR';
            shouldRetry = true;
            break;
          case 502:
            errorMessage =
              'Bad gateway. The server is temporarily unavailable.';
            errorCode = 'BAD_GATEWAY';
            shouldRetry = true;
            break;
          case 503:
            errorMessage = 'Service unavailable. Please try again later.';
            errorCode = 'SERVICE_UNAVAILABLE';
            shouldRetry = true;
            break;
          case 504:
            errorMessage =
              'Gateway timeout. The request took too long to process.';
            errorCode = 'GATEWAY_TIMEOUT';
            shouldRetry = true;
            break;
          default:
            errorMessage = `HTTP error ${statusCode}. Please try again later.`;
            errorCode = 'HTTP_ERROR';
            shouldRetry = statusCode >= 500;
        }
      }
      // Response parsing errors
      else if (
        error.message?.includes('JSON') ||
        error.name === 'SyntaxError'
      ) {
        errorMessage = 'Invalid response format from server.';
        errorCode = 'PARSE_ERROR';
        shouldRetry = false;
      }
      // Stream reading errors
      else if (
        error.message?.includes('stream') ||
        error.message?.includes('reader')
      ) {
        errorMessage = 'Error reading response stream. Please try again.';
        errorCode = 'STREAM_ERROR';
        shouldRetry = true;
      }
      // Generic error with message
      else if (error.message) {
        errorMessage = error.message;
        errorCode = 'GENERIC_ERROR';
        shouldRetry = true;
      }

      // If this is the last attempt or shouldn't retry, throw the error
      if (attempt === maxRetries || !shouldRetry) {
        const errorDetails = {
          message: errorMessage,
          code: errorCode,
          statusCode: statusCode,
          originalError: error.message,
          timestamp: new Date().toISOString(),
          attempts: attempt,
        };

        console.error('Final Error Details:', errorDetails);
        throw errorDetails;
      }

      // Wait before retrying (exponential backoff)
      const waitTime = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
      console.log(`Retrying in ${waitTime}ms...`);
      await delay(waitTime);
    }
  }
}

/**
 * Checks if a URL is a YouTube link
 * @param {string} url - URL to check
 * @returns {boolean} True if YouTube link
 */
function isYouTubeLink(url) {
  try {
    const parsedUrl = new URL(url);
    return (
      parsedUrl.hostname.includes('youtube.com') ||
      parsedUrl.hostname.includes('youtu.be')
    );
  } catch (error) {
    return false;
  }
}

/**
 * Creates prompt for article generation
 * @param {string} content - Content to base article on
 * @param {number} minWords - Minimum word count
 * @returns {string} Formatted prompt
 */
function createArticlePrompt(content, minWords = 600) {
  return `
    ${content}

    Compose long article consists of minimum ${minWords} words about the content above.
    ${ARTICLE_STYLE_INSTRUCTION}
    Make it in a document ready to copy.
    ${OUTPUT_FORMAT_INSTRUCTION}
  `;
}

/**
 * Generates article content from a summary
 * @param {string} summary - Summary to expand into article
 * @returns {Promise} Generated article content
 */
export async function generateContentFromSummary(summary) {
  if (!summary || typeof summary !== 'string') {
    throw new Error('Summary is required and must be a string');
  }

  const prompt = createArticlePrompt(`Summary: ${summary}.`);

  return makeGeminiRequest({
    contents: prompt,
    config: {
      tools: [{ urlContext: {} }],
    },
  });
}

/**
 * Generates article content from a trend topic
 * @param {string} trend - Trend topic to write about
 * @returns {Promise} Generated article content
 */
export async function generateArticleContent(trend) {
  if (!trend || typeof trend !== 'string') {
    throw new Error('Trend is required and must be a string');
  }

  const prompt = createArticlePrompt(`Topic: ${trend}`);

  return makeGeminiRequest({
    contents: prompt,
    config: {
      tools: [{ urlContext: {} }],
    },
  });
}

/**
 * Creates request configuration for YouTube video summarization
 * @param {string} link - YouTube video URL
 * @returns {Object} Request configuration
 */
function createYouTubeRequest(link) {
  const prompt = `
    Summarize the content of this YouTube video, make the summarized text as short as possible and tell the main point of the video and using Indonesian Language
    ${OUTPUT_FORMAT_INSTRUCTION_FOR_YOUTUBE_LINK}
  `;

  return {
    contents: [
      {
        role: 'user',
        parts: [
          { text: prompt },
          {
            fileData: {
              fileUri: link,
              mimeType: 'video/*',
            },
          },
        ],
      },
    ],
    config: {
      tools: [{ urlContext: {} }],
    },
  };
}

/**
 * Creates request configuration for webpage summarization
 * @param {string} link - Webpage URL
 * @returns {Object} Request configuration
 */
function createWebpageRequest(link) {
  const prompt = `
    Summarize the main points from the following web page. Focus on key information and insights: ${link}. using Indonesian Language
    ${OUTPUT_FORMAT_INSTRUCTION_FOR_YOUTUBE_LINK}
  `;

  return {
    contents: [
      {
        role: 'user',
        parts: [{ text: prompt }],
      },
    ],
    config: {
      tools: [{ urlContext: {} }],
    },
  };
}

/**
 * Summarizes content from a link (YouTube or webpage)
 * @param {string} link - URL to summarize
 * @returns {Promise} Summarized content
 */
export async function summarizeContentFromLink(link) {
  if (!link || typeof link !== 'string') {
    throw new Error('Link is required and must be a string');
  }

  const requestData = isYouTubeLink(link)
    ? createYouTubeRequest(link)
    : createWebpageRequest(link);

  return makeGeminiRequest(requestData);
}

/**
 * Generates SEO-friendly titles from content
 * @param {string} content - Content to generate titles from
 * @param {number} count - Number of titles to generate
 * @param {number} maxLength - Maximum character length per title
 * @returns {Promise} Generated titles
 */
export async function generateTitleFromContent(
  content,
  count = 10,
  maxLength = 70
) {
  if (!content || typeof content !== 'string') {
    throw new Error('Content is required and must be a string');
  }

  const prompt = `
    Generate ${count} creative and SEO-friendly article titles from content: "${content}".

    Make sure the titles are engaging and under ${maxLength} characters each.
    Return the titles as a numbered list in Indonesian language.
    
      Output Format Requirements:
        - Do not include Markdown or triple backticks or any other special characters.
  `;

  return makeGeminiRequest({
    contents: prompt,
    config: {
      tools: [{ urlContext: {} }],
    },
  });
}

// Export configuration for testing purposes
export const config = {
  API_URL,
  TYPE,
  DEFAULT_MODEL,
};
